/**
 * Die **Reiter-Form**: die Vereinigung aus aktivem Reiter und Karte darunter —
 * ein geschlossener SVG-Pfad.
 *
 * Zwei Aufrufer, dieselbe Form: der Fensterrahmen (`ContentUnionShape`, der
 * aktive Tab wächst in die Inhaltskarte) und der **Bereich** auf dem
 * Projekt-Canvas (`AreaNode`, der Reiter mit dem Namen sitzt oben links auf der
 * Karte). Hier herausgezogen, damit der Bereich die Kurve nicht ein zweites
 * Mal zeichnet; die Bezier-Punkte der S-Kurve stammen aus dem Figma-Union-SVG
 * (`tabBarConstants.ts`).
 *
 * Reines Rechenwerk ohne React, absichtlich VERBATIM aus `ContentUnionShape`
 * übernommen — `unionPath.test.ts` hält die Ausgabe für alle vier Fälle fest.
 */

import { CURVE_WIDTH, R_SEG, L_SEG } from '../../navigation/TabBar/tabBarConstants'

/** Radius der Kartenecken. */
export const UNION_CORNER_RADIUS = 12
const R = UNION_CORNER_RADIUS
const K = 0.5522847 // Bezier approximation constant for quarter circle
/** Wie weit die S-Kurve je Seite in den Reiter hineingezogen wird, damit der Buckel schmaler ist als der Reiter. */
export const BUMP_INSET = 28

/**
 * Builds a closed SVG path for the union of active tab + content rectangle.
 * Returns empty string if content rect is not available.
 */
export function buildUnionPath(
  content: { x: number; y: number; w: number; h: number },
  tab: { x: number; y: number; w: number; h: number } | null,
  _isFirstTab: boolean,
): string {
  const { x: cX, y: cY, w: cW, h: cH } = content

  if (cW <= 0 || cH <= 0) return ''

  // No visible tab → simple rounded rectangle
  if (!tab) {
    return roundedRect(cX, cY, cW, cH, R)
  }

  const tabRight = tab.x + tab.w
  const tabTop = tab.y
  const tabHeight = cY - tabTop // Distance from tab top to content top

  if (tabHeight <= 0) {
    return roundedRect(cX, cY, cW, cH, R)
  }

  // Inset the effective tab edges to make the bump narrower
  const effectiveTabX = tab.x + BUMP_INSET
  const effectiveTabRight = tabRight - BUMP_INSET
  const effectiveTabW = effectiveTabRight - effectiveTabX

  // Safety: if tab is too narrow for inset, skip bump
  if (effectiveTabW < 0) {
    return roundedRect(cX, cY, cW, cH, R)
  }

  const CW = CURVE_WIDTH
  const cornerR = 12

  // Auto-detect overflow: use simple corner when S-curve would exceed content bounds.
  // Both sides use purely geometric detection so the transition happens
  // gradually as the bump slides toward the edge (no sudden snap).
  const simpleLeft = (effectiveTabX - CW < cX)
  const simpleRight = (effectiveTabRight + CW > cX + cW)

  if (simpleLeft && simpleRight) {
    // Both sides: simple 12px corners (no S-curves)
    const contentRight = cX + cW
    return [
      `M ${cX},${cY}`,
      `L ${cX},${tabTop + cornerR}`,
      `C ${cX},${tabTop + cornerR * (1 - K)} ${cX + cornerR * (1 - K)},${tabTop} ${cX + cornerR},${tabTop}`,
      `L ${contentRight - cornerR},${tabTop}`,
      `C ${contentRight - cornerR + cornerR * K},${tabTop} ${contentRight},${tabTop + cornerR * (1 - K)} ${contentRight},${tabTop + cornerR}`,
      `L ${contentRight},${cY + cH - R}`,
      `C ${contentRight},${cY + cH - R + R * K} ${contentRight - R + R * K},${cY + cH} ${contentRight - R},${cY + cH}`,
      `L ${cX + R},${cY + cH}`,
      `C ${cX + R - R * K},${cY + cH} ${cX},${cY + cH - R + R * K} ${cX},${cY + cH - R}`,
      `L ${cX},${cY}`,
      'Z',
    ].join(' ')
  }

  if (simpleLeft) {
    // Left: simple 12px corner, Right: S-curve
    return [
      `M ${cX},${cY}`,
      `L ${cX},${tabTop + cornerR}`,
      `C ${cX},${tabTop + cornerR * (1 - K)} ${cX + cornerR * (1 - K)},${tabTop} ${cX + cornerR},${tabTop}`,
      `L ${effectiveTabRight},${tabTop}`,
      ...R_SEG.map(s => {
        const sx = effectiveTabRight + s.c1x * CW
        const sy = tabTop + s.c1y * tabHeight
        const cx = effectiveTabRight + s.c2x * CW
        const cy = tabTop + s.c2y * tabHeight
        const ex = effectiveTabRight + s.ex * CW
        const ey = tabTop + s.ey * tabHeight
        return `C ${sx},${sy} ${cx},${cy} ${ex},${ey}`
      }),
      `L ${cX + cW - R},${cY}`,
      `C ${cX + cW - R + R * K},${cY} ${cX + cW},${cY + R * (1 - K)} ${cX + cW},${cY + R}`,
      `L ${cX + cW},${cY + cH - R}`,
      `C ${cX + cW},${cY + cH - R + R * K} ${cX + cW - R + R * K},${cY + cH} ${cX + cW - R},${cY + cH}`,
      `L ${cX + R},${cY + cH}`,
      `C ${cX + R - R * K},${cY + cH} ${cX},${cY + cH - R + R * K} ${cX},${cY + cH - R}`,
      `L ${cX},${cY}`,
      'Z',
    ].join(' ')
  }

  if (simpleRight) {
    // Left: S-curve, Right: simple 12px corner
    const tabLeftEdge = effectiveTabX - CW
    const contentRight = cX + cW
    return [
      `M ${cX + R},${cY}`,
      `C ${cX + R - R * K},${cY} ${cX},${cY + R * (1 - K)} ${cX},${cY + R}`,
      `L ${cX},${cY + cH - R}`,
      `C ${cX},${cY + cH - R + R * K} ${cX + R - R * K},${cY + cH} ${cX + R},${cY + cH}`,
      `L ${contentRight - R},${cY + cH}`,
      `C ${contentRight - R + R * K},${cY + cH} ${contentRight},${cY + cH - R + R * K} ${contentRight},${cY + cH - R}`,
      `L ${contentRight},${tabTop + cornerR}`,
      `C ${contentRight},${tabTop + cornerR * (1 - K)} ${contentRight - cornerR + cornerR * K},${tabTop} ${contentRight - cornerR},${tabTop}`,
      `L ${effectiveTabX},${tabTop}`,
      ...reverseSCurve(L_SEG, tabLeftEdge, tabTop, CW, tabHeight),
      `L ${cX + R},${cY}`,
      'Z',
    ].join(' ')
  }

  // Normal: left S-curve, right S-curve
  const tabLeftEdge = effectiveTabX - CW
  return [
    `M ${cX + R},${cY}`,
    `C ${cX + R - R * K},${cY} ${cX},${cY + R * (1 - K)} ${cX},${cY + R}`,
    `L ${cX},${cY + cH - R}`,
    `C ${cX},${cY + cH - R + R * K} ${cX + R - R * K},${cY + cH} ${cX + R},${cY + cH}`,
    `L ${cX + cW - R},${cY + cH}`,
    `C ${cX + cW - R + R * K},${cY + cH} ${cX + cW},${cY + cH - R + R * K} ${cX + cW},${cY + cH - R}`,
    `L ${cX + cW},${cY + R}`,
    `C ${cX + cW},${cY + R * (1 - K)} ${cX + cW - R + R * K},${cY} ${cX + cW - R},${cY}`,
    `L ${effectiveTabRight + CW},${cY}`,
    ...reverseSCurve(R_SEG, effectiveTabRight, tabTop, CW, tabHeight),
    `L ${effectiveTabX},${tabTop}`,
    ...reverseSCurve(L_SEG, tabLeftEdge, tabTop, CW, tabHeight),
    `L ${cX + R},${cY}`,
    'Z',
  ].join(' ')
}

/** Reverse S-curve segments for tracing the path in the opposite direction */
function reverseSCurve(
  segments: readonly { c1x: number; c1y: number; c2x: number; c2y: number; ex: number; ey: number }[],
  originX: number,
  originY: number,
  curveWidth: number,
  height: number,
): string[] {
  // Walk segments in reverse, swapping control points
  const result: string[] = []
  for (let i = segments.length - 1; i >= 0; i--) {
    const s = segments[i]
    const prev = i > 0 ? segments[i - 1] : { ex: 0, ey: segments === R_SEG ? 0 : 1 }

    // For R_SEG: origin is at (0,0)=tab-top-right, (1,1)=content-level
    // For L_SEG: origin is at (0,1)=content-level, (1,0)=tab-top-left
    const c1x = originX + s.c2x * curveWidth
    const c1y = originY + s.c2y * height
    const c2x = originX + s.c1x * curveWidth
    const c2y = originY + s.c1y * height
    const ex = originX + prev.ex * curveWidth
    const ey = originY + prev.ey * height

    result.push(`C ${c1x},${c1y} ${c2x},${c2y} ${ex},${ey}`)
  }
  return result
}

/** Simple rounded rectangle path */
export function roundedRect(x: number, y: number, w: number, h: number, r: number): string {
  return [
    `M ${x + r},${y}`,
    `L ${x + w - r},${y}`,
    `C ${x + w - r + r * K},${y} ${x + w},${y + r * (1 - K)} ${x + w},${y + r}`,
    `L ${x + w},${y + h - r}`,
    `C ${x + w},${y + h - r + r * K} ${x + w - r + r * K},${y + h} ${x + w - r},${y + h}`,
    `L ${x + r},${y + h}`,
    `C ${x + r - r * K},${y + h} ${x},${y + h - r + r * K} ${x},${y + h - r}`,
    `L ${x},${y + r}`,
    `C ${x},${y + r * (1 - K)} ${x + r - r * K},${y} ${x + r},${y}`,
    'Z',
  ].join(' ')
}
